import React, {useContext, useState} from 'react';
import {withRouter} from 'react-router';
import {Anchor, Banner, Heading} from '@albert-io/atomic';

import useAreLatestQueriesReady from 'lib/hooks/useAreLatestQueriesReady';
import {history} from 'client/history';
import sessionStore from 'client/Session/SessionStore';
import {upperFirst} from 'lib/stringUtils';

import type {
  AssignmentModelV3,
  QuestionModelV3,
  StandardModelV1,
  StudentModelV2
} from '@albert-io/models';

import {ReportsContext} from '../Reports.context';
import Breadcrumbs, {Crumb} from '../Breadcrumbs/Breadcrumbs.react';
import Headings from '../Breadcrumbs/BreadcrumbHeadings.react';
import {TOP_LEVEL_FILTERS} from '../reports.types';
import {
  DIMENSIONS,
  getClassroomQuery,
  getDistrictQuery,
  getSchoolQuery,
  getTeacherQuery,
  isDimension
} from '../reports.utils';
import SelectDropdown from '../SelectDropdown/SelectDropdown.react';

import './drilldown.scss';

interface Props {
  location: {
    pathname: string;
    search?: string;
  };
  headingData: AssignmentModelV3 | QuestionModelV3 | StandardModelV1 | StudentModelV2;
  defaultValue: string;
  dimensionInFocus: string;
}

function Drilldown({location, dimensionInFocus, headingData, defaultValue}: Props) {
  const {topLevelFilterType, topLevelFilterId, path} = useContext(ReportsContext);
  const [isOpen, setIsOpen] = useState(false);

  const isClassroom = topLevelFilterType === 'classrooms';
  const isSchool = topLevelFilterType === 'schools';
  const isDistrict = topLevelFilterType === 'districts';
  const isTeacher = topLevelFilterType === 'teachers';
  const isOtherTeacher = isTeacher && topLevelFilterId !== sessionStore.getUserId();

  let queries: any[] = [];

  if (isClassroom) {
    queries = [getClassroomQuery(topLevelFilterId)];
  } else if (isTeacher) {
    queries = [getTeacherQuery(topLevelFilterId)];
  } else if (isSchool) {
    queries = [getSchoolQuery(topLevelFilterId)];
  } else if (isDistrict) {
    queries = [getDistrictQuery(topLevelFilterId)];
  }

  /* Fetches classroom */
  const {isReady} = useAreLatestQueriesReady(queries);
  const activeClassroom = isClassroom && getClassroomQuery(topLevelFilterId).getResource();
  const activeTeacher = isTeacher && getTeacherQuery(topLevelFilterId).getResource();
  const activeSchool = isSchool && getSchoolQuery(topLevelFilterId).getResource();
  const activeDistrict = isDistrict && getDistrictQuery(topLevelFilterId).getResource();

  if (!isReady) {
    return null;
  }

  const breadcrumbTrail = [...path];
  const DrilldownHeading = Headings[dimensionInFocus];
  const breadcrumbs = breadcrumbTrail.reduce((prev: React.ReactNode[], curr, index) => {
    if (isDimension(curr) || curr === 'gradebook') {
      const href = breadcrumbTrail.slice(0, index + 1).join('/') + location.search;
      prev.push(
        <Crumb key={curr} to={href}>
          {upperFirst(curr)}
        </Crumb>
      );
      const nextItem = breadcrumbTrail[index + 1];
      if (nextItem && curr !== 'gradebook') {
        const Next = Breadcrumbs[curr];
        const nextItemHref = `${breadcrumbTrail.slice(0, index + 1).join('/')}/${nextItem}${
          location.search
        }`;
        prev.push(<Next id={nextItem} key={nextItem} to={nextItemHref} />);
      }
    }
    return prev;
  }, []);

  const getDimensionOptions = () => {
    return Object.keys(DIMENSIONS).map((val) => ({
      disabled: path.includes(val) && path[path.length - 1] !== val,
      key: val,
      value: val,
      selected: path[path.length - 1] === val,
      label: upperFirst(val)
    }));
  };

  return (
    <>
      <div className='u-align-items_center u-display_flex u-flex-wrap_wrap'>
        <ul className='reports__drilldown-nav__list u-bgc_slate-050 u-color_slate-400 u-border-b u-border-color_slate-200 u-pad-lr_5 u-display_flex u-flex-wrap_wrap u-list-style_none'>
          <Crumb key={topLevelFilterId} to={`/reports/${topLevelFilterType}/${topLevelFilterId}/`}>
            {isClassroom &&
              activeClassroom.getStatus() === 'archived' &&
              `${activeClassroom.getName()} (Archived)`}

            {isClassroom && activeClassroom.getStatus() !== 'archived' && activeClassroom.getName()}

            {isTeacher && !isOtherTeacher && 'My Classes'}
            {isOtherTeacher &&
              `${activeTeacher.getFirstName()} ${activeTeacher.getLastName()}'s Classes`}

            {isSchool && activeSchool.getName()}

            {isDistrict && activeDistrict.getName()}
          </Crumb>
          {breadcrumbs}
        </ul>
        <div>
          {breadcrumbTrail.includes('gradebook') ? (
            <Banner className='u-pad-t_1 u-pad-lr_5'>
              <Banner.Icon icon='sparkles' iconStyle='regular' />
              <div className='u-display_flex u-flex-direction_column'>
                <h4 className='u-mar-b_1'>Feature Preview</h4>
                <span>
                  Gradebook will be part of the “Analytics Upgrade” available in the 2024-2025
                  school year. Learn more about the feature{' '}
                  <Anchor
                    href='https://help.albert.io/en/articles/6683815-how-do-i-use-reports-for-data-analysis#h_8b75842d2c'
                    rel='noopener noreferrer'
                    target='_blank'
                    className='u-text-decoration_underline'
                    inheritColor
                  >
                    here
                  </Anchor>
                  .
                </span>
              </div>
            </Banner>
          ) : null}
          <div className='u-display_flex u-align-items_center u-flex-wrap_wrap u-pad-t_3 u-pad-b_2 u-pad-lr_5'>
            {TOP_LEVEL_FILTERS.includes(dimensionInFocus) ? (
              <Heading size='xl' className='u-text-transform_capitalize'>
                {breadcrumbTrail[breadcrumbTrail.length - 1]}
              </Heading>
            ) : (
              <DrilldownHeading className='u-mar-r_2 u-white-space_nowrap' data={headingData} />
            )}
            {/* Do not show dropdown selector when on top-level dimension since it can be selected via sidebar */}
            {!TOP_LEVEL_FILTERS.includes(dimensionInFocus) && (
              <SelectDropdown
                label='Choose a filter'
                key='dimension-select'
                defaultValue={defaultValue}
                onSelect={(option) => {
                  const nextDimension = option.value;
                  const nextPath = [...path];
                  nextPath.pop();
                  nextPath.push(nextDimension);
                  history.pushState(null, `${nextPath.join('/')}${location.search}`);
                }}
                options={getDimensionOptions()}
                isOpen={isOpen}
                setIsOpen={setIsOpen}
              />
            )}
          </div>
        </div>
      </div>
    </>
  );
}

export default withRouter(Drilldown);
